import React, { useState } from "react";
import { styled, alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";
import Sitemark from "./SitemarkIcon";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: "blur(24px)",
  border: "1px solid",
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: "8px 12px",
}));

export default function AppAppBar({ onTabChange }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false); // État pour le Drawer mobile

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const handleSignOut = () => {
    logout();
    navigate("/");
  };

  const isAdminDashboard = location.pathname.startsWith("/AdminDashboard");

  const menuItems = [
    { label: t("menu.features"), section: "features" },
    { label: t("menu.highlight"), section: "highlight" },
    { label: t("menu.pricing"), section: "pricing" },
    { label: t("menu.faq"), section: "faq" },
  ];

  if (isAdminDashboard) {
    return null;
  }

  return (
    <>
      {/* Version Desktop */}
      <AppBar
        position="fixed"
        enableColorOnDark
        sx={{
          boxShadow: 0,
          bgcolor: "transparent",
          backgroundImage: "none",
          mt: "calc(var(--template-frame-height, 0px) + 28px)",
          display: { xs: "none", md: "block" }, // Masqué sur mobile
        }}
      >
        <Container maxWidth="lg">
          <StyledToolbar>
            <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", px: 0 }}>
              <Sitemark />
              <Box sx={{ display: { xs: "none", md: "flex" } }}>
                {menuItems.map((item) => (
                  <Button
                    key={item.section}
                    variant="text"
                    color="info"
                    size="small"
                    onClick={() => scrollToSection(item.section)}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {user.isAuthenticated ? (
                <>
                  <Button
                    variant="text"
                    color="info"
                    size="small"
                    sx={{ minWidth: 0 }}
                    component={RouterLink}
                    to="/AdminDashboard"
                  >
                    {t("menu.myWorkspace")}
                  </Button>
                  <Button color="primary" variant="contained" size="small" onClick={handleSignOut}>
                    {t("menu.logout")}
                  </Button>
                </>
              ) : (
                <>
                  <Button color="primary" variant="outlined" size="small" component={RouterLink} to="/SignUpPage">
                    {t("menu.signUp")}
                  </Button>
                  <Button color="primary" variant="contained" size="small" component={RouterLink} to="/SignInPage">
                    {t("menu.login")}
                  </Button>
                </>
              )}
            </Box>
          </StyledToolbar>
        </Container>
      </AppBar>

      {/* Version Mobile */}
      <AppBar
        position="fixed"
        enableColorOnDark
        sx={{
          boxShadow: 0,
          bgcolor: "transparent",
          backgroundImage: "none",
          mt: "calc(var(--template-frame-height, 0px) + 28px)",
          display: { xs: "block", md: "none" }, // Visible uniquement sur mobile
        }}
      >
        <Container maxWidth="lg">
          <StyledToolbar>
            <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
              <Sitemark />
            </Box>
            <IconButton sx={{right: "16px"}} color="inherit" aria-label="open drawer" edge="end" onClick={toggleMobileMenu}>
              <MenuIcon />
            </IconButton>
          </StyledToolbar>
        </Container>

        {/* Drawer Mobile */}
        <Drawer anchor="right" open={isMobileOpen} onClose={toggleMobileMenu}>
          <Box
            sx={{
              width: 250,
              display: "flex",
              flexDirection: "column",
              padding: 2,
            }}
          >
            <List>
              {menuItems.map((item) => (
                <ListItem
                  button
                  key={item.section}
                  onClick={() => {
                    scrollToSection(item.section);
                    toggleMobileMenu();
                  }}
                >
                  <Typography>{item.label}</Typography>
                </ListItem>
              ))}
              {user.isAuthenticated ? (
                <>
                  <ListItem
                    button
                    component={RouterLink}
                    to="/AdminDashboard"
                    onClick={toggleMobileMenu}
                  >
                    <Typography>{t("menu.myWorkspace")}</Typography>
                  </ListItem>
                  <ListItem button onClick={handleSignOut}>
                    <Typography>{t("menu.logout")}</Typography>
                  </ListItem>
                </>
              ) : (
                <>
                  <ListItem
                    button
                    component={RouterLink}
                    to="/SignUpPage"
                    onClick={toggleMobileMenu}
                  >
                    <Typography>{t("menu.signUp")}</Typography>
                  </ListItem>
                  <ListItem
                    button
                    component={RouterLink}
                    to="/SignInPage"
                    onClick={toggleMobileMenu}
                  >
                    <Typography>{t("menu.login")}</Typography>
                  </ListItem>
                </>
              )}
            </List>
          </Box>
        </Drawer>
      </AppBar>
    </>
  );
}

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};